import React from 'react'

import styled from '@emotion/styled'

import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { Button, Switch, Typography } from 'antd'

import { Box } from 'common/components/boxes'
import { DrawerTableFilters } from 'common/components/DrawerTableFilters'
import { usePsqlTable } from 'common/components/PsqlTable/psql_table_provider'
import { SelectFilter } from 'common/components/table/postgres_filters'
import { usePersistentFilters } from 'common/contexts/persistent_filters'

export interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values?: { [key: string]: any }
  facets?: { [key: string]: string | Date }
}

const ListItem = styled(Box)`
  transition: background 0.3s;
  &:hover {
    background-color: ${({ theme }) => theme.colors['gray-3']};
  }
`

export function OrdersFilters(props: Props) {
  const { values = {}, facets = [] } = props

  const persistentFilters = usePersistentFilters()
  const { columns } = usePsqlTable()

  const optionsFactory = React.useCallback(
    (key: string, nameAttributeKey: string) => {
      return (facets[key] || []).map((x) => ({
        label: x[nameAttributeKey],
        value: x[nameAttributeKey],
        count: x.count,
      }))
    },
    [facets],
  )

  return (
    <DrawerTableFilters
      title="Order Options"
      useAlgolia={false}
      appliedFiltersCounter={persistentFilters.appliedFiltersCount}
    >
      <Box mb={16} display="flex" justifyContent="space-between" alignItems="center">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Filters
        </Typography.Title>

        <Button
          type="link"
          data-cy="clear-filters"
          onClick={() => persistentFilters.handleClearFilters()}
          disabled={persistentFilters.appliedFiltersCount === 0}
        >
          Clear All
        </Button>
      </Box>

      <Box mb={16} width="100%" display="flex" flexDirection="column">
        <Typography.Text type="secondary">Watchers</Typography.Text>

        <SelectFilter
          items={optionsFactory('watcher_name', 'watcher_name')}
          selected={values.watcher}
          onChange={(value) => persistentFilters.handleChangeFilters({ watcher: value })}
          attribute="watcher"
          placeholder="Select watchers"
          isSearchable
        />
      </Box>

      <Box mb={16} width="100%" display="flex" flexDirection="column">
        <Typography.Text type="secondary">Project</Typography.Text>

        <SelectFilter
          items={optionsFactory('project_names', 'project_name')}
          selected={values.project}
          onChange={(value) => persistentFilters.handleChangeFilters({ project: value })}
          attribute="project"
          placeholder="Select project"
          isSearchable
        />
      </Box>

      <Box>
        <Box width="100%">
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mb={16}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Columns
            </Typography.Title>
            <Button
              type="link"
              onClick={() => columns.forEach((column) => column.toggleHidden(false))}
              disabled={columns.every((column) => column.isVisible)}
            >
              Show All
            </Button>
          </Box>

          {columns.map((column) => {
            return (
              <ListItem
                key={column.id}
                mb={4}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => column.toggleHidden(column.isVisible)}
                py={4}
                px={6}
                mx={-6}
                borderRadius="md"
                style={{ cursor: 'pointer' }}
                backgroundColor={column.isVisible ? 'transparent' : 'gray-4'}
              >
                <Typography.Text>{column.Header || column.id}</Typography.Text>
                <Switch
                  size="small"
                  checked={column.isVisible}
                  checkedChildren={<EyeOutlined />}
                  unCheckedChildren={<EyeInvisibleOutlined />}
                />
              </ListItem>
            )
          })}
        </Box>
      </Box>
    </DrawerTableFilters>
  )
}
